import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Landing from '../Esg/Landing';
import { SERVER_URL } from "../../config/config";

// Format date from ISO string to "Month Day, Year"
const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

// Transform API whitepaper data to component format
const transformWhitepaperData = (apiWhitepaper) => {
    const publicationSource = apiWhitepaper.publishedAt || apiWhitepaper.createdAt;

    return {
        title: apiWhitepaper.title || "",
        description: apiWhitepaper.subheading || "",
        publicationDate: publicationSource ? formatDate(publicationSource) : "",
        length: apiWhitepaper.pdfPageCount ||"-",
        language: "English",
        pdfUrl: apiWhitepaper.pdfUrl || "#",
    };
};

const WhitepaperCard = ({ item }) => {
    return (
        <a
            href={item.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col md:flex-row bg-white rounded-xl shadow-[0_4px_20px_0_rgba(0,0,0,0.08)] p-6 gap-6 hover:shadow-[0_8px_30px_0_rgba(0,0,0,0.12)] transition-shadow duration-300 w-full mb-6 cursor-pointer group"
        >
            <div className="flex-shrink-0 flex items-center justify-center bg-[#F9F9F9] rounded-lg p-4 w-full md:w-[150px] group-hover:bg-[#F2F2F2] transition-colors">
                <div className="relative">
                    <svg width="80" height="100" viewBox="0 0 80 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 0H60L80 20V90C80 95.5228 75.5228 100 70 100H10C4.47715 100 0 95.5228 0 90V10C0 4.47715 4.47715 0 10 0Z" fill="#F3F3F3" />
                        <path d="M60 0V20H80L60 0Z" fill="#E0E0E0" />
                        <rect x="15" y="40" width="50" height="4" rx="2" fill="#D9D9D9" />
                        <rect x="15" y="52" width="50" height="4" rx="2" fill="#D9D9D9" />
                        <rect x="15" y="64" width="50" height="4" rx="2" fill="#D9D9D9" />
                        <rect x="15" y="76" width="30" height="4" rx="2" fill="#D9D9D9" />
                        <rect x="5" y="10" width="35" height="20" rx="4" fill="#FF3B30" />
                        <text x="10" y="24" fill="white" fontSize="10" fontWeight="bold" fontFamily="Arial">PDF</text>
                    </svg>
                </div>
            </div>
            <div className="flex flex-col flex-grow gap-3">
                <h3 className="text-xl font-bold text-black group-hover:text-[#1C486B] transition-colors">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed line-clamp-2 md:line-clamp-none">
                    {item.description}
                </p>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-semibold text-gray-800 mt-2">
                    <p><span className="text-gray-500 font-medium">Publication date:</span> {item.publicationDate}</p>
                    <p><span className="text-gray-500 font-medium">Length:</span> {item.length}</p>
                    <p><span className="text-gray-500 font-medium">Language:</span> {item.language}</p>
                </div>
            </div>
        </a>
    );
};

const Whitepaper = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [whitepapers, setWhitepapers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchWhitepapers = async () => {
            try {
                const response = await fetch(`${SERVER_URL}/api/whitepaper/whitepaperForWebsite`);
                const result = await response.json();

                if (result.data && Array.isArray(result.data)) {
                    const transformed = result.data.map(transformWhitepaperData);
                    setWhitepapers(transformed);
                } else {
                    setWhitepapers([]);
                }
            } catch (error) {
                console.error("Error fetching whitepapers:", error);
                setWhitepapers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchWhitepapers();
    }, []);

    return (
        <>
            <Helmet>
                <title>Whitepapers | ESG Insights & Sustainability Research | Nirantara</title>
                <meta name="description" content="Explore our collection of expert whitepapers on ESG trends, sustainability reporting, and climate action. Gain deep insights into the future of responsible business." />
                <link rel="canonical" href="https://nirantara.solutions/whitepaper" />
            </Helmet>

            <div className="flex flex-col items-center w-full montserrat">
                <Landing
                    text="WHITEPAPER"
                    isMobile={isMobile}
                    image="/whitepaper.jpg"
                />

                <div className="flex flex-col items-center w-full gap-10 sm:gap-14 px-[2rem] py-[2rem] sm:px-[5rem] md:px-[5rem] lg:px-[15rem] montserrat bg-[#FFF]">
                    <div className="w-full">
                        <h2 className="text-3xl font-bold text-black mb-10 uppercase tracking-tight">WHITEPAPER</h2>

                        {loading ? (
                            <div className="text-center py-8 text-gray-600">
                                Loading whitepapers...
                            </div>
                        ) : whitepapers.length === 0 ? (
                            <div className="text-center py-8 text-gray-600">
                                No whitepapers available at the moment.
                            </div>
                        ) : (
                            <div className="flex flex-col w-full">
                                {whitepapers.map((item, index) => (
                                    <WhitepaperCard key={index} item={item} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Whitepaper;
